import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { moviesApi } from "@/api/movies";
import { musicApi } from "@/api/music";
import { tvApi } from "@/api/tv";
import { cn } from "@/lib/cn";

/**
 * Command palette. Three concept layers:
 *
 *   1. NAVIGATION — go-to-page commands, always present
 *   2. ACTIONS    — verbs (Add movie, Run TV scan, …)
 *   3. MEDIA      — filtered movies / series / artists when there's a query
 *
 * Lazy-fetches the three lists on open; while empty/blank query we
 * just show navigation + actions.
 *
 * Filtering is a simple substring match on lowercased title/sort
 * fields. We don't bother with fuzzy ranking — the lists are small
 * (hundreds at most), and a substring match is more predictable for
 * power users.
 */

interface Props {
  open: boolean;
  onClose: () => void;
}

type CommandKind = "nav" | "action" | "movie" | "series" | "artist";

interface Command {
  id: string;
  kind: CommandKind;
  label: string;
  hint?: string;
  /** Optional secondary line (e.g. year, type). */
  meta?: string;
  /** Action to run on enter. Receives a navigate fn for convenience. */
  run: (nav: ReturnType<typeof useNavigate>) => void;
}

const navCommands: Command[] = [
  {
    id: "nav-dashboard",
    kind: "nav",
    label: "Dashboard",
    hint: "g h",
    run: (nav) => nav({ to: "/" }),
  },
  {
    id: "nav-movies",
    kind: "nav",
    label: "Movies",
    hint: "g m",
    run: (nav) => nav({ to: "/movies" }),
  },
  {
    id: "nav-tv",
    kind: "nav",
    label: "TV",
    hint: "g t",
    run: (nav) => nav({ to: "/tv" }),
  },
  {
    id: "nav-music",
    kind: "nav",
    label: "Music",
    hint: "g a",
    run: (nav) => nav({ to: "/music" }),
  },
  {
    id: "nav-search",
    kind: "nav",
    label: "Manual search",
    hint: "/",
    run: (nav) => nav({ to: "/search" }),
  },
  {
    id: "nav-wanted-movies",
    kind: "nav",
    label: "Wanted (Movies)",
    run: (nav) => nav({ to: "/movies/wanted" }),
  },
  {
    id: "nav-wanted-tv",
    kind: "nav",
    label: "Wanted (TV)",
    run: (nav) => nav({ to: "/tv/wanted" }),
  },
  {
    id: "nav-queue",
    kind: "nav",
    label: "Download queue",
    run: (nav) => nav({ to: "/nzb/queue" }),
  },
  {
    id: "nav-history",
    kind: "nav",
    label: "Download history",
    run: (nav) => nav({ to: "/nzb/history" }),
  },
];

const settingsCommands: Command[] = [
  {
    id: "set-indexers",
    kind: "action",
    label: "Settings: Indexers",
    run: (nav) => nav({ to: "/settings/indexers" }),
  },
  {
    id: "set-clients",
    kind: "action",
    label: "Settings: Download clients",
    run: (nav) => nav({ to: "/settings/download-clients" }),
  },
  {
    id: "set-quality",
    kind: "action",
    label: "Settings: Quality profiles",
    run: (nav) => nav({ to: "/settings/quality" }),
  },
  {
    id: "set-movies",
    kind: "action",
    label: "Settings: Movies",
    run: (nav) => nav({ to: "/settings/movies" }),
  },
  {
    id: "set-tv",
    kind: "action",
    label: "Settings: TV",
    run: (nav) => nav({ to: "/settings/tv" }),
  },
  {
    id: "set-music",
    kind: "action",
    label: "Settings: Music",
    run: (nav) => nav({ to: "/settings/music" }),
  },
  {
    id: "set-tmdb",
    kind: "action",
    label: "Settings: TMDB",
    run: (nav) => nav({ to: "/settings/tmdb" }),
  },
];

const kindLabels: Record<CommandKind, string> = {
  nav: "Go to",
  action: "Action",
  movie: "Movie",
  series: "Series",
  artist: "Artist",
};

export function CommandPalette({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Lazy-load media lists when the palette opens.
  const movies = useQuery({
    queryKey: ["movies"],
    queryFn: () => moviesApi.list(),
    enabled: open,
    staleTime: 60_000,
  });
  const series = useQuery({
    queryKey: ["series"],
    queryFn: tvApi.list,
    enabled: open,
    staleTime: 60_000,
  });
  const artists = useQuery({
    queryKey: ["music", "artists"],
    queryFn: musicApi.listArtists,
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Focus on next tick after render commits.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const lowerQ = query.trim().toLowerCase();

  const mediaCommands = useMemo<Command[]>(() => {
    if (!lowerQ) return [];
    const out: Command[] = [];
    if (movies.data) {
      for (const m of movies.data) {
        if (m.title.toLowerCase().includes(lowerQ)) {
          out.push({
            id: `movie-${m.id}`,
            kind: "movie",
            label: m.title,
            meta: m.year ? String(m.year) : undefined,
            run: (nav) =>
              nav({ to: "/movies/$id", params: { id: String(m.id) } }),
          });
        }
        if (out.length > 8) break;
      }
    }
    if (series.data) {
      for (const s of series.data) {
        if (s.title.toLowerCase().includes(lowerQ)) {
          out.push({
            id: `series-${s.id}`,
            kind: "series",
            label: s.title,
            meta: s.first_air_date?.slice(0, 4),
            run: (nav) =>
              nav({ to: "/tv/$id", params: { id: String(s.id) } }),
          });
        }
        if (out.length > 16) break;
      }
    }
    if (artists.data) {
      for (const a of artists.data) {
        if (a.name.toLowerCase().includes(lowerQ)) {
          out.push({
            id: `artist-${a.id}`,
            kind: "artist",
            label: a.name,
            run: (nav) =>
              nav({ to: "/music/artists/$id", params: { id: String(a.id) } }),
          });
        }
        if (out.length > 24) break;
      }
    }
    return out;
  }, [lowerQ, movies.data, series.data, artists.data]);

  const navItems = lowerQ
    ? navCommands.filter((c) => c.label.toLowerCase().includes(lowerQ))
    : navCommands;
  const actionItems = lowerQ
    ? settingsCommands.filter((c) => c.label.toLowerCase().includes(lowerQ))
    : settingsCommands;

  const groups = [
    { kind: "nav" as const, items: navItems },
    { kind: "action" as const, items: actionItems },
    {
      kind: "movie" as const,
      items: mediaCommands.filter((c) => c.kind === "movie"),
    },
    {
      kind: "series" as const,
      items: mediaCommands.filter((c) => c.kind === "series"),
    },
    {
      kind: "artist" as const,
      items: mediaCommands.filter((c) => c.kind === "artist"),
    },
  ].filter((g) => g.items.length > 0);

  // Flat list of commands for arrow-key navigation.
  const flat = groups.flatMap((g) => g.items);
  const clampedIndex = Math.min(activeIndex, Math.max(0, flat.length - 1));

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const cmd = flat[clampedIndex];
        if (cmd) {
          cmd.run(navigate);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, flat, clampedIndex, navigate, onClose]);

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 animate-backdrop-in bg-black/60 backdrop-blur-md"
        aria-hidden
      />
      <div
        className="relative w-full max-w-xl animate-modal-in overflow-hidden rounded-lg border border-line bg-surface/95 shadow-elev backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search row */}
        <div className="flex items-center gap-3 border-b border-line/70 px-4 py-3">
          <svg
            className="h-4 w-4 text-foreground/45"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            aria-hidden
          >
            <circle cx={11} cy={11} r={7} />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Type a command, page, or media title…"
            className="flex-1 bg-transparent text-sm placeholder:text-foreground/35 focus:outline-none"
            aria-label="Command palette query"
          />
          <kbd className="font-mono text-[10px] text-foreground/40">esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {flat.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-foreground/55">
              {lowerQ
                ? `No results for "${query}".`
                : "Start typing to search."}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.kind} className="mb-2">
                <div className="eyebrow px-4 py-1.5">{kindLabels[g.kind]}</div>
                <ul>
                  {g.items.map((cmd) => {
                    runningIndex += 1;
                    const isActive = runningIndex === clampedIndex;
                    return (
                      <li key={cmd.id}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIndex(runningIndex)}
                          onClick={() => {
                            cmd.run(navigate);
                            onClose();
                          }}
                          className={cn(
                            "flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition",
                            isActive
                              ? "bg-accent/15 text-foreground"
                              : "text-foreground/85 hover:bg-surface-2/60",
                          )}
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <KindGlyph kind={cmd.kind} active={isActive} />
                            <span className="truncate">{cmd.label}</span>
                            {cmd.meta && (
                              <span className="shrink-0 font-mono text-[11px] text-foreground/45">
                                {cmd.meta}
                              </span>
                            )}
                          </span>
                          {cmd.hint && (
                            <span className="shrink-0 font-mono text-[10px] text-foreground/40">
                              {cmd.hint}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-line/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
          <span>↑↓ navigate · ↵ run</span>
          <span>arr-in-one</span>
        </div>
      </div>
    </div>
  );
}

function KindGlyph({
  kind,
  active,
}: {
  kind: CommandKind;
  active: boolean;
}) {
  const c = active ? "text-accent" : "text-foreground/40";
  switch (kind) {
    case "movie":
      return (
        <svg
          className={cn("h-3.5 w-3.5 shrink-0", c)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          aria-hidden
        >
          <rect x={2} y={3} width={12} height={10} rx={1.5} />
          <path d="M2 6h12M2 10h12M5 3v10M11 3v10" />
        </svg>
      );
    case "series":
      return (
        <svg
          className={cn("h-3.5 w-3.5 shrink-0", c)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          aria-hidden
        >
          <rect x={2} y={4} width={12} height={9} rx={1.5} />
          <path d="m6 1 2 3 2-3" strokeLinecap="round" />
        </svg>
      );
    case "artist":
      return (
        <svg
          className={cn("h-3.5 w-3.5 shrink-0", c)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          aria-hidden
        >
          <circle cx={6} cy={11} r={2.2} />
          <path d="M8.2 11V3l5 1.2v6" />
          <circle cx={11} cy={10} r={2.2} />
        </svg>
      );
    case "action":
      return (
        <svg
          className={cn("h-3.5 w-3.5 shrink-0", c)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          aria-hidden
        >
          <circle cx={8} cy={8} r={6} />
          <path
            d="M8 5v3l2 2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "nav":
    default:
      return (
        <svg
          className={cn("h-3.5 w-3.5 shrink-0", c)}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          aria-hidden
        >
          <path
            d="m6 4 4 4-4 4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
}
